import { Flex, Box, Heading } from '@chakra-ui/react';
import NextLink from 'next/link';
import { FC, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import useSWRInfinite from 'swr/infinite';

import {
  NotificationItem,
  NotificationItemSkeleton,
} from '@/components/ui/shared/notifications/NotificationItem/NotificationItem';
import { ResourceType } from '@/enums';
import { fetchNotifications } from '@/fetchers/notifications';
import { useQueryParams, StringParam } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { Notification } from '@/resources/Notification';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

import { getNotificationResourceLink, getKey } from './Notifications.utils';
export interface INotificationsProps {
  pageSize?: number;
  refreshInterval?: number;
  resourceType?: ResourceType | Array<ResourceType>;
}

export const Notifications: FC<INotificationsProps> = ({
  pageSize = 10,
  refreshInterval = 60 * 1000,
  resourceType = ResourceType.Lecture,
}) => {
  const { t } = useTranslation();
  const { store } = useStores();
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data, error, size, setSize } = useSWRInfinite(
    getKey,
    (cursorId) => fetchNotifications(store, pageSize, eventId, cursorId, resourceType),
    {
      refreshInterval: refreshInterval,
    }
  );

  const { items, isLoadingMore, isReachingEnd } = useMemo(
    () => extractSWRInfiniteData<Notification>(data, error, size),
    [data, error, size]
  );

  const row = useMemo(
    () => (_, item) => {
      return (
        <NextLink href={getNotificationResourceLink(eventId, item)} passHref legacyBehavior>
          <NotificationItem key={item.id} as="a" notification={item} px={{ base: 8, md: 5 }} />
        </NextLink>
      );
    },
    [eventId]
  );

  return (
    <Flex h="full" flexDirection="column">
      <Heading fontSize="2xl" px={{ base: 8, md: 5 }} py={{ base: 6, md: 6 }}>
        {t('notifications.title')}
      </Heading>
      <Box flex={1}>
        <Virtuoso
          style={{ paddingBottom: '32px' }}
          data={items}
          endReached={() => !isLoadingMore && !isReachingEnd && setSize(size + 1)}
          itemContent={row}
          components={{
            Footer: () => {
              if (isLoadingMore) {
                return (
                  <Fragment>
                    {Array.from({ length: 10 }, (_, i) => (
                      <NotificationItemSkeleton key={i} px={{ base: 8, md: 5 }} />
                    ))}
                  </Fragment>
                );
              }

              return null;
            },
          }}
        />
      </Box>
    </Flex>
  );
};
